import React, { useState, useEffect, useRef } from 'react';
import { useNavigate, NavLink } from "react-router-dom";
import { Product } from '../models/product';
import { FieldUpdateType, NoteType, DocumentType, Entities, GravimetricCalculationType, ProductState } from '../models/enums';
import ProductSafetyComponent from './ProductSafetyComponent';
import ProductRawMaterialsData from './Production/ProductRawMaterialsData';
import TextNoteEditor from './Common/TextNoteEditor';
import { NewProductObject } from '../models/Production/new-product-object';
import { NewProductRequest } from '../models/Production/new-product-request';
import { ProductListItem } from '../models/product-list-item';
import { BatchListItem } from '../models/batch-list-item';
import { LookupData } from '../models/lookup-data';
import { LookupItem } from '../models/lookup-item';
import { Batch } from '../models/batch';
import { Vendor } from '../models/vendor';
import { FieldUpdate } from '../models/field-update';
import { AnalysisScope } from '../models/AnalyticalPlans/analysis-scope';
import { MolecularDataFieldUpdate } from '../models/molecular-data-field-update';
import AddBatchComponent from './AddBatchComponent';
import { SymbolEditorProps } from '../models/symbol-editor-props';
import { TextSymbolEditorData } from '../models/text-symbol-editor-data';
import { ItemBasicInformation } from '../models/item-basic-information';
import { ModalMetadata } from '../models/modal-metadata';
import { ModalMessageType } from '../models/enums';
import NoteViewer from './NoteViewer';
import MolecularDataComponent from './MolecularDataComponent';
import ProductCompositionsData from './ProductCompositionsData';
import ProductAnalyticalPlansData from './ProductAnalyticalPlansData';
import { RenderingFunctions } from '../utilities/rendering-functions';
import { PWMath } from '../utilities/pw-math';
import { DataTemplates } from '../templates/data-templates';
import ProductStandardsData from './ProductStandardsData';
import TextSymbolEditor from './TextSymbolEditor';
import AuditHistoryComponent from './AuditHistoryComponent';
import { CrudFunctions } from '../utilities/crud-functions';
import ItemBasicInformationDialog from './ItemBasicInformationDialog';
import TicketLogEntryForm from './TicketLogEntryForm';
import { ProductConstants } from '../store/product-constants';
import DocumentLoader from './DocumentLoader';
import { UserDataStore } from '../store/userDataStore';
import GlobalDialog from './Modal/GlobalDialog';
import { DocumentationConstants } from '../store/documentation-constants';
import BenchSheetToolbar from './BenchSheetToolbar';
import DmaFooter from './DmaFooter';

import DataGrid, {
    Column,
    Selection,
    Paging,
    Toolbar,
    Item as ToolBarItem,
    Button as ColumnButton,
    LoadPanel
} from 'devextreme-react/data-grid';

import TreeView, { TreeViewRef } from 'devextreme-react/tree-view';

import {
    Validator,
    StringLengthRule,
    RangeRule
} from 'devextreme-react/validator';

import TextBox, { Button as TextBoxButton } from 'devextreme-react/text-box';
import NumberBox from 'devextreme-react/number-box';
import SelectBox from 'devextreme-react/select-box';
import CheckBox from 'devextreme-react/check-box';
import List from 'devextreme-react/list';
import Button from 'devextreme-react/button';
import { Popup as DialogPopup } from 'devextreme-react/popup';

//temp
const maximumFillUnits = [
    { id: 1, unit: 'mL' },
    { id: 4, unit: 'grams' },
    { id: 9, unit: 'µL' }
];

//temp
const productStatesList = [
    { id: 1, name: 'Raw' },
    { id: 2, name: 'Bulk' },
    { id: 3, name: 'Intermediate' },
    { id: 4, name: 'Finished Good' }
];

const unitSizeUomAttr = { id: 'unitSizeUomInput' };
const formAttr = { id: 'formInput' };
const packageControlAttr = { id: 'packageControlInput' };
const storageAttr = { id: 'storageInput' };
const shippingConditionAttr = { id: 'shippingConditionInput' };
const unitsDisplayedOnCoaAttr = { id: 'unitsDisplayedOnCoaInput' };
const gravimetricCalculationAttr = { id: 'gravimetricCalculationInput' };
const preparationDesignAttr = { id: 'preparationDesignInput' };

let key = 0;

export default function ProductForm() {
    const navigate = useNavigate();
    const productStatesTreeViewRef = useRef<TreeViewRef<ProductListItem>>(null);

    const [product, setProduct] =
        useState<Product>();

    const [productStates, setProductStates] =
        useState<ProductListItem[]>();

    const [productBatches, setProductBatches] =
        useState<BatchListItem[]>();

    const [productAnalysisScopesSelectedItemKeys, setProductAnalysisScopesSelectedItemKeys] =
        useState<number[]>();

    const [productDataUpdatedIdx, setProductDataUpdatedIdx] =
        useState<number>(1);

    const [lookupData, setLookupData] =
        useState<LookupData>();

    const [vendors, setVendors] =
        useState<Vendor[]>();

    const [selectedBatch, setSelectedBatch] =
        useState<BatchListItem>();

    const [copyBatchSourceBatchId, setCopyBatchSourceBatchId] =
        useState<string>();

    const [auditEntity, setAuditEntity] =
        useState<Entities>();

    const [auditRowPointer, setAuditRowPointer] =
        useState<string>();

    const [itemBasicInformation, setItemBasicInformation] =
        useState<ItemBasicInformation>();

    const [symbolEditorProps, setSymbolEditorProps] =
        useState<SymbolEditorProps>();

    const [globalDialogMetadata, setGlobalDialogMetadata] =
        useState<ModalMetadata>();

    const [newProductObject, setNewProductObject] =
        useState<NewProductObject>();

    const [canAddNewProductState, setCanAddNewProductState] =
        useState<boolean>(false);

    const [addProductStateDialogVisible, setAddProductStateDialogVisible] =
        useState<boolean>();

    const [newProductUrl, setNewProductUrl] =
        useState<string>();

    const [newBatchPromptVisible, setNewBatchPromptVisible] =
        useState<boolean>();

    const [auditInformationDialogVisible, setAuditInformationDialogVisible] =
        useState<boolean>();

    const [ticketLogEntryDialogVisible, setTicketLogEntryDialogVisible] =
        useState<boolean>();

    const [auditInformationShowChildData, setAuditInformationShowChildData] =
        useState<boolean>();

    const [itemBasicInformationDialogVisible, setItemBasicInformationDialogVisible] =
        useState<boolean>();

    const [globalDialogVisible, setGlobalDialogVisible] =
        useState<boolean>();

    const [analyticalTabsVisible, setAnalyticalTabsVisible] =
        useState<boolean>(false);

    // const [dataLoaded, setDataLoaded] =
    //   useState<boolean>();

    const [productStateUiUpdated, setProductStateUiUpdated] =
        useState<boolean>();

    useEffect(() => {
        initializeUI();
        getAllData();
    }, []);

    useEffect(() => {
        updateProductStatesChanges();
    }, [product, productStates]);

    const parts = window.location.href.split('/');
    const productId = parts[parts.length - 1];

    ++key;
    const gridWidth = 'calc(90vw - 13em)';

    const user = UserDataStore.getCurrentUser();
    const canEditRoles = ["SA", "MF-A", "MF-L", "MF-T", "PP-A", "PP-L", "PP-T", "DIST-A", "BA-A", "QCP-A", "QCD-A", "RD-A", "RD-L", "RD-T"];
    const canEdit = canEditRoles.some(r => user.roleCodes && user.roleCodes.includes(r));
    const canInsertRoles = ["SA", "MF-A", "PP-A", "PP-L", "DIST-A", "RD-A", "RD-L"];
    const canInsert = canInsertRoles.some(r => user.roleCodes && user.roleCodes.includes(r));
    const canDeleteRoles = ["SA", "MF-A", "PP-A", "RD-A"];
    const canDelete = canDeleteRoles.some(r => user.roleCodes && user.roleCodes.includes(r));

    const nameLabelButton = {
        text: 'Ω',
        stylingMode: 'text',
        width: 32,
        hint: "Insert Symbol",
        onClick: () => {
            nameLabelTextSymbolButton();
        }
    }

    const longNameButton = {
        text: 'Ω',
        stylingMode: 'text',
        width: 32,
        hint: "Insert Symbol",
        elementAttr: {
            id: 'longName'
        },
        onClick: () => {
            longNameTextSymbolButton();
        }
    }

    const nominalUnitSize = () => {
        if (product && product.nominalUnitSize
            && product.unitSizeUom && product.unitSizeUom.id
            && lookupData && lookupData.uoms) {

            const uomId = product.unitSizeUom.id;
            const matchUom = lookupData.uoms.filter(u => u.id === uomId);

            if (matchUom && matchUom.length > 0)
                return `${product.nominalUnitSize} ${matchUom[0].name}`;

            return 'NA';
        }
        return 'NA';
    }

    const fillInstructionsRender = () => {
        if (product && product.unitSizeUom && product.unitSizeUom.id) {
            let targetFillUomString = '';
            const uomId = product.unitSizeUom.id;

            if (uomId) {
                let selectedUom = maximumFillUnits.filter(u => u.id === uomId);

                if (selectedUom && selectedUom.length > 0)
                    targetFillUomString = selectedUom[0].unit;
            }

            return <div>
                {product.targetFill} ± {product.targetFillUncertainty} {targetFillUomString}
            </div>;
        }
    };

    let batchWeightsList = () => {
        if (product && product.batchDensities &&
            product.batchDensities.length > 0) {
            return <div>{
                product.batchDensities.map((item, i) =>
                    <div className="batch-weight-item pw-margin-left-05"
                        key={i.toString()}>
                        <div>{item.batchName}</div>
                        <div>{item.density}</div>
                    </div>)
            }</div>;
        }
    }

    let storageTemperature = () => {
        let temperature = 'NA';

        if (lookupData && lookupData.storageTypes) {
            const matchingTemperature = lookupData.storageTypes.find(st =>
                product && product.storage &&
                product.storage.id && st.id === product.storage.id);

            if (matchingTemperature && matchingTemperature.description)
                temperature = matchingTemperature.description;
        }

        return temperature;
    }

    let productFamily = () => {
        const formId = product && product.form && product.form.id ?
            product.form.id : undefined;

        let form = 'NA';
        let expirationString = 'NA';
        let storageTemperature = 'NA';
        let storageCondition = 'NA';

        if (formId) {
            switch (formId) {
                case 1:
                    form = ProductConstants.FormConstants.Neat;
                    break;
                case 2:
                    form = ProductConstants.FormConstants.MixedSolution;
                    break;
                case 3:
                    form = ProductConstants.FormConstants.MixedSolid;
                    break;
            }
        }

        if (product && product.shelfLife && product.shelfLife.expiration) {
            if (product.shelfLife.expiration < 13)
                expirationString = '12';
            else if (product.shelfLife.expiration >= 13 && product.shelfLife.expiration < 25)
                expirationString = '24';
            else if (product.shelfLife.expiration >= 25 && product.shelfLife.expiration < 37)
                expirationString = '36';
            else
                expirationString = '48';
        }

        if (lookupData && lookupData.storageTypes) {
            const storageMatch = lookupData.storageTypes.find(s =>
                product && product.storage && s.id === product.storage.id);

            if (storageMatch && storageMatch.name)
                storageTemperature = storageMatch.name;
        }

        const shippingConditionId = product && product.shippingCondition && product.shippingCondition.id ?
            product.shippingCondition.id : undefined;

        if (shippingConditionId) {
            switch (shippingConditionId) {
                case 1:
                    storageCondition = ProductConstants.ShippingConditionConstants.Ambient;
                    break;
                case 2:
                    storageCondition = ProductConstants.ShippingConditionConstants.WetIce;
                    break;
                case 3:
                    storageCondition = ProductConstants.ShippingConditionConstants.DryIce;
                    break;
            }
        }

        return `${storageCondition}-${form}-${expirationString}-${storageTemperature}`;
    }


    let humidityInformation = () => {
        if (!product)
            return "";

        let humidity = "";

        if (product.protectFromLight)
            humidity += "Protect from light";

        if (product.protectFromHumidity) {
            humidity += `${humidity && ', '}Protect from humidity,`;
            humidity += product.productionHumidityRequirement;
        }

        return humidity;
    }


    let slcForm = () => {
        if (lookupData && lookupData.shippingConditionTypes) {

            const matchingForm = lookupData.formTypes.find(f =>
                product && product.form && product.form.id &&
                f.id === product.form.id);

            if (matchingForm)
                return matchingForm.name;
        }

        return "NA";
    }

    let slcShippingCondition = () => {
        if (lookupData && lookupData.shippingConditionTypes) {

            const matchingShippingCondition = lookupData.shippingConditionTypes.find(s =>
                product && product.shippingCondition && product.shippingCondition.id &&
                s.id === product.shippingCondition.id);

            if (matchingShippingCondition)
                return matchingShippingCondition.name;
        }

        return "NA";
    }

    let productStateColor = () => {
        if (product && product.productState) {
            let cssClass = RenderingFunctions.returnProductStateColor(product.productState);
            return `pw-readonly-label ${cssClass}`;
        }
        return "pw-readonly-label";
    }

    let newProductStateColor = () => {
        if (newProductObject) {
            let cssClass = RenderingFunctions.returnProductStateColor(newProductObject.productState);
            return `pw-readonly-label ${cssClass}`;
        }
        return "pw-readonly-label";
    }

    let productState = () => {
        if (product && product.productState)
            return product.productState;
        return null;
    }

    if (!product)
        return <div>LOADING</div>;

    return (
        <div className="pw-content">
            <div id="pb-title-pane">
                <div className="lq-title-bar">
                    <BenchSheetToolbar title="Product Planning"
                        entity={Entities.Products}
                        rightInfo="Catalog #"
                        benchSheetId=''
                        rightInfoSubtitle={product && product.catalogNumber ?
                            product.catalogNumber : ''}
                        auditRowPointer={productId}
                        documentationId={DocumentationConstants.ProductsForm}
                        showItemInformation={showProductInformationDialog} />

                </div>
            </div>




            <div id="tab-buttons-panel" className="tab-buttons-container">
                <div id="mainPanelButton" className="tab-button" onClick={onPanelButtonClick}><span className="material-symbols-outlined tab-icon pw-blue">hdr_weak</span> Main</div>
                <div id="molecularDataPanelButton" className="tab-button-inactive" onClick={onPanelButtonClick}><span className="material-symbols-outlined tab-icon pw-blue">steppers</span> Molecular Data</div>
                <div id="deaPanelButton" className="tab-button-inactive" onClick={onPanelButtonClick}><span className="material-symbols-outlined tab-icon pw-blue">local_police</span> DEA</div>
                <div id="shelfLifeClaimPanelButton" className="tab-button-inactive" onClick={onPanelButtonClick}><span className="material-symbols-outlined tab-icon pw-blue">timer</span> SLC</div>
                <div id="safetyPanelButton" className="tab-button-inactive" onClick={onPanelButtonClick}><span className="material-symbols-outlined tab-icon pw-blue">health_and_safety</span> Safety</div>
                <div id="productionPanelButton" className="tab-button-inactive" onClick={onPanelButtonClick}><span className="material-symbols-outlined tab-icon pw-blue">build</span> Production</div>

                {analyticalTabsVisible &&
                    <div id="compositionsPanelButton" className="tab-button-inactive" onClick={onPanelButtonClick}><span className="material-symbols-outlined tab-icon pw-blue">format_list_bulleted</span> Compositions</div>
                }

                {analyticalTabsVisible &&
                    <div id="compStandardsPanelButton" className="tab-button-inactive" onClick={onPanelButtonClick}><span className="material-symbols-outlined tab-icon pw-blue">fact_check</span> Standards</div>
                }

                {analyticalTabsVisible &&
                    <div id="analyticalPlansPanelButton" className="tab-button-inactive" onClick={onPanelButtonClick}><span className="material-symbols-outlined tab-icon pw-blue">assignment</span> Analytical Plan</div>
                }

                {analyticalTabsVisible &&
                    <div id="coaPanelButton" className="tab-button-inactive" onClick={onPanelButtonClick}><span className="material-symbols-outlined tab-icon pw-blue">task</span> COA</div>
                }


                {/*
                {(product && (product.productState !== ProductState.Raw && product.productState !== ProductState.FinishedGood)) &&
                    <div id="compositionsPanelButton" className="tab-button-inactive" onClick={onPanelButtonClick}><span className="material-symbols-outlined tab-icon pw-blue">format_list_bulleted</span> Compositions</div>
                }

                {(product && (product.productState !== ProductState.Raw && product.productState !== ProductState.FinishedGood)) &&
                    <div id="compStandardsPanelButton" className="tab-button-inactive" onClick={onPanelButtonClick}><span className="material-symbols-outlined tab-icon pw-blue">fact_check</span> Standards</div>
                }

                {(product && (product.productState !== ProductState.Raw && product.productState !== ProductState.FinishedGood)) &&
                    <div id="analyticalPlansPanelButton" className="tab-button-inactive" onClick={onPanelButtonClick}><span className="material-symbols-outlined tab-icon pw-blue">assignment</span> Analytical Plan</div>
                }

                {(product && (product.productState !== ProductState.Raw && product.productState !== ProductState.FinishedGood)) &&
                    <div id="coaPanelButton" className="tab-button-inactive" onClick={onPanelButtonClick}><span className="material-symbols-outlined tab-icon pw-blue">task</span> COA</div>
                }
                */}


                <div id="distributionPanelButton" className="tab-button-inactive" onClick={onPanelButtonClick}><span className="material-symbols-outlined tab-icon pw-blue">account_tree</span> Distribution</div>
                <div id="handlingPanelButton" className="tab-button-inactive" onClick={onPanelButtonClick}><span className="material-symbols-outlined tab-icon pw-blue">box</span> Handling</div>
                <div id="batchesPanelButton" className="tab-button-inactive" onClick={onPanelButtonClick}><span className="material-symbols-outlined tab-icon pw-blue">widgets</span> Batches</div>
                <div id="notesPanelButton" className="tab-button-inactive" onClick={onPanelButtonClick}><span className="material-symbols-outlined tab-icon pw-blue">edit_note</span> Notes and Documents</div>
            </div>

            <div>
                <div className="form-container">
                    <div id="mainPanel" className="pw-form-section">
                        <div className="pw-comp-row">

                            <div className="pw-form-unit pw-comp-10">
                                <div className="pw-form-unit-label">Part#</div>
                                <TextBox id="partNumber" className="textbox"
                                    inputAttr={{ id: "txtPartNumber" }}
                                    readOnly={!canEdit}
                                    onValueChanged={onProductValueChanged}
                                    placeholder='NA'
                                    value={product ? product.partNumber : undefined} >
                                    <Validator>
                                        <StringLengthRule message="Part Number must be between 3 and 100 characters in length." min={3} max={100} trim={true} />
                                    </Validator>
                                </TextBox>
                            </div>

                            <div className="pw-form-unit pw-comp-20">
                                <div className="pw-form-unit-label">Product State</div>
                                <div id="productStateDiv" className={productStateColor()}>{product ? product.productStateName : ''}</div>
                            </div>

                        </div>

                        {(product && product.productState > ProductState.Bulk) &&
                            <div className="pw-comp-row">
                                <div className="pw-form-unit pw-comp-10">
                                    <div className="pw-form-unit-label">Pack Size</div>
                                    <NumberBox id="nominalUnitSize"
                                        inputAttr={{ id: "txtPackSize" }}
                                        readOnly={!canEdit}
                                        className="textbox"
                                        onValueChanged={onProductValueChanged}
                                        format="####0.###"
                                        placeholder='NA'
                                        value={product ? product.nominalUnitSize : undefined} >
                                        <Validator>
                                            <RangeRule message="Pack Size must be between 0 and 99999.999" min={0} max={99999.999} />
                                        </Validator>
                                    </NumberBox>
                                </div>

                                <div className="pw-form-unit pw-comp-10">
                                    <div className="pw-form-unit-label">UOM</div>
                                    <SelectBox id="unitSizeUom" className="selectbox"
                                        placeholder='NA'
                                        inputAttr={{ id: "txtUom" }}
                                        readOnly={!canEdit}
                                        itemRender={DataTemplates.ProductUomItemRender}
                                        displayExpr={'name'} valueExpr={'id'}

                                        dataSource={lookupData && lookupData.uoms ?
                                            lookupData.uoms : []}
                                        onValueChanged={onProductLookupItemSelectionChanged}
                                        value={product && product.unitSizeUom ?
                                            product.unitSizeUom.id : null} />
                                </div>

                                <div className="pw-form-unit pw-comp-10">
                                    <div className="pw-form-unit-label">Units per Package</div>

                                    <NumberBox id="unitsMultiplier"
                                        inputAttr={{ id: "txtUnitsPerPackage" }}
                                        readOnly={!canEdit}
                                        className="textbox"
                                        onValueChanged={onProductValueChanged}
                                        format="####0.#"
                                        placeholder='NA'
                                        value={product ? product.unitsMultiplier : undefined} >
                                    </NumberBox>
                                </div>
                            </div>
                        }

                        <div className="pw-form-unit">
                            <div className="pw-form-unit-label">Name Label</div>
                            <TextBox id="nameLabel" className="textbox"
                                inputAttr={{ id: "txtNameLabel" }}
                                readOnly={!canEdit}
                                onValueChanged={onProductValueChanged}
                                placeholder='NA' value={product ? product.nameLabel : undefined}>
                                <TextBoxButton name="nameLabelSubscriptButton"
                                    location="after"
                                    options={nameLabelButton as any} />
                                <Validator>
                                    <StringLengthRule message="Name Label must be less than 300 characters in length." max={300} trim={true} />
                                </Validator>
                            </TextBox>
                        </div>

                        <div className="pw-form-unit">
                            <div className="pw-form-unit-label">Long Name</div>
                            <TextBox id="longName" className="textbox"
                                inputAttr={{ id: "txtLongName" }}
                                readOnly={!canEdit}
                                onValueChanged={onProductValueChanged}
                                placeholder='NA'
                                value={product ? product.longName : undefined}>
                                <TextBoxButton name="longNameSubscriptButton"
                                    location="after"
                                    options={longNameButton as any} />
                                <Validator>
                                    <StringLengthRule message="Long Name must be less than 300 characters in length." max={300} trim={true} />
                                </Validator>
                            </TextBox>
                        </div>

                        {(productState() === ProductState.Bulk || productState() === ProductState.Intermediate) &&
                            <div className="pw-comp-row">
                                <div className="pw-form-unit pw-comp-20">
                                    <div className="pw-form-unit-label">Applicable Characterizations</div>
                                    <List items={lookupData && lookupData.scopeTypes ?
                                        lookupData.scopeTypes : []} keyExpr="id"
                                        className="dx-listbox"
                                        height="12rem" selectionMode="multiple"
                                        showSelectionControls={true}
                                        itemRender={analysisScopesRender}
                                        onOptionChanged={productAnalysisScopeTypesChanged}
                                        selectedItemKeys={productAnalysisScopesSelectedItemKeys ?
                                            productAnalysisScopesSelectedItemKeys : []} />
                                </div>
                            </div>
                        }

                        <div className="lq-sub-title pw-margin-left-05 pw-margin-top-1 pw-margin-bottom-05">Product States</div>

                        <div>
                            <TreeView id="product-states-treeview" ref={productStatesTreeViewRef}
                                items={productStates}
                                dataStructure="plain"

                                itemRender={productStatesTreeViewitemRender}
                                parentIdExpr="parentId"
                                keyExpr="productId"
                                width="30rem" />
                        </div>

                        <div className="pw-comp-row pw-margin-top-1 pw-margin-left-05">
                            <Button className="pw-form-unit"
                                icon="sortup" text="Add New State" disabled={!canEdit || !canAddNewProductState}
                                type="success" hint="Add new product state"
                                width='10rem' height='2rem'
                                onClick={addProductState} />
                        </div>

                        {/* TODO: need to navigate, then reload the page - window.location.reload();
                        {newProductUrl &&
                            <div className="pw-margin-top-1">
                                <NavLink to={newProductUrl} className="datagrid-icon-link"><span className="dx-icon-edit datagrid-icon">GO TO NEW PRODUCT</span></NavLink>
                            </div>
                        }
                        */}

                        <DmaFooter />

                    </div>

                    <div id="molecularDataPanel" className="pw-form-section tab-full-width">
                        <div className="pw-form-section">
                            <div className="lq-sub-title pw-margin-left-05 pw-margin-top-05 pw-margin-bottom-05">Molecular Data</div>

                            <MolecularDataComponent canEdit={canEdit}
                                productId={product && product.productId ? product.productId : undefined} />

                        </div>
                    </div>

                    <div id="deaPanel" className="pw-form-section tab-full-width">
                        <div className="pw-form-section">
                            <div className="lq-sub-title pw-margin-left-05 pw-margin-top-05 pw-margin-bottom-05">DEA Information</div>

                            <div className="pw-comp-row">
                                <div className="pw-form-unit pw-comp-20">
                                    <div className="pw-form-unit-label">DEA Schedule</div>
                                    <TextBox id="deaSchedule" className="textbox"
                                        readOnly={!canEdit}
                                        onValueChanged={onProductValueChanged}
                                        placeholder='NA'
                                        value={product ? product.deaSchedule : ''} >
                                    </TextBox>
                                </div>
                            </div>

                            <div className="pw-comp-row">
                                <div className="pw-form-unit pw-comp-20">
                                    <div className="pw-form-unit-label">DEA Drug Code</div>
                                    <TextBox id="deaDrugCode" className="textbox"
                                        readOnly={!canEdit}
                                        onValueChanged={onProductValueChanged}
                                        placeholder='NA'
                                        value={product ? product.deaDrugCode : ''} >
                                    </TextBox>
                                </div>
                            </div>

                            <div className="pw-comp-row">
                                <div className="pw-form-unit pw-comp-20">
                                    <div className="pw-form-unit-label">DEA Exemption</div>
                                    <TextBox id="deaExemption" className="textbox"
                                        readOnly={!canEdit}
                                        onValueChanged={onProductValueChanged}
                                        placeholder='NA'
                                        value={product ? product.deaExemption : ''} >
                                    </TextBox>
                                </div>

                            </div>

                        </div>
                    </div>

                    <div id="shelfLifeClaimPanel" className="pw-form-section tab-full-width">
                        <div className="pw-form-section">
                            <div className="lq-sub-title pw-margin-left-05 pw-margin-top-05 pw-margin-bottom-05">Shelf Life Claim</div>

                            <div className="pw-comp-row">
                                <div className="pw-form-unit pw-comp-10">
                                    <CheckBox id="noSlcRequired" className="pw-check-box"
                                        text="No SLC Required"
                                        onValueChanged={shelfLifeFieldChanged}
                                        value={product && product.shelfLife ?
                                            product.shelfLife.noSlcRequired : false} />
                                </div>

                                {product && product.shelfLife &&
                                    product.shelfLife.noSlcRequired &&
                                    <div className="pw-form-unit pw-comp-30">
                                        <div className="pw-form-unit-label">Reason</div>
                                        <TextBox id="noSlcRequiredReason" className="textbox"
                                            showClearButton={true}
                                            placeholder='NA'
                                            value={product && product.shelfLife ?
                                                product.shelfLife.noSlcRequiredReason : undefined}
                                            onValueChanged={shelfLifeFieldChanged} >
                                        </TextBox>
                                    </div>
                                }
                            </div>

                            <div className="pw-comp-row">
                                <div className="pw-form-unit pw-comp-10">
                                    <div className="pw-form-unit-label">Product Family</div>
                                    <div className="pw-readonly-label">{productFamily()}</div>
                                </div>
                                <div className="pw-form-unit pw-comp-15">
                                    <CheckBox id="productFamilyValidated" className="pw-check-box"
                                        text="Product Family Validated"
                                        onValueChanged={shelfLifeFieldChanged}
                                        value={product && product.shelfLife ?
                                            product.shelfLife.productFamilyValidated : false} />
                                </div>
                            </div>

                            <div className="pw-comp-row">
                                <div className="pw-form-unit pw-comp-10">
                                    <div className="pw-form-unit-label">Shipping Condition</div>
                                    <div className="pw-readonly-label">{slcShippingCondition()}</div>
                                </div>

                                <div className="pw-form-unit pw-comp-10">
                                    <div className="pw-form-unit-label">Form</div>
                                    <div className="pw-readonly-label">{slcForm()}</div>
                                </div>

                                <div className="pw-form-unit pw-comp-10">
                                    <div className="pw-form-unit-label">Expiration (months)</div>
                                    <NumberBox id="expiration"
                                        className="textbox"
                                        width={'10em'}
                                        onValueChanged={onProductValueChanged}
                                        format="#0"
                                        value={product && product.expiration ?
                                            product.expiration : 0} >
                                        <Validator>
                                            <RangeRule message="Expiration (months) must be between 0 and 60." min={0} max={60} />
                                        </Validator>
                                    </NumberBox>
                                </div>
                            </div>

                            <div>
                                <div className="lq-header pw-margin-top-05">
                                    <div className="lq-header-title">Supporting Documents</div>
                                </div>

                                <DocumentLoader parentId={productId}
                                    documentType={DocumentType.ShelfLifeSupport}
                                    canDelete={canDelete} />
                            </div>

                            <DmaFooter />

                        </div>
                    </div>

                    <div id="safetyPanel" className="pw-form-section">
                        <ProductSafetyComponent productId={productId} canEdit={canEdit} />
                        <DmaFooter />
                    </div>

                    <div id="productionPanel" className="pw-form-section">
                        <div className="lq-sub-title pw-margin-left-05 pw-margin-top-05 pw-margin-bottom-05">Production</div>

                        <div className="pw-comp-row">
                            <div className="pw-form-unit pw-comp-8">
                                <div className="pw-form-unit-label">Pack Size</div>
                                <div className="pw-readonly-inline-label">{nominalUnitSize()}</div>
                            </div>

                            <div className="pw-form-unit">
                                <div className="pw-form-unit-label">Target Fill ± Tolerance</div>
                                <div className="pw-plus-minus-container">
                                    <NumberBox id="targetFill"
                                        format="#0.000" readOnly={!canEdit}
                                        onValueChanged={onProductValueChanged}
                                        value={product ? product.targetFill : undefined} />
                                    <div className="pw-readonly-label pw-auto-margin"> ± </div>

                                    <NumberBox id="targetFillUncertainty"
                                        format="#0.000" readOnly={!canEdit}
                                        onValueChanged={onProductValueChanged}
                                        value={product ? product.targetFillUncertainty : undefined} />
                                </div>
                            </div>

                            <div className="pw-form-unit pw-comp-5">
                                <div className="pw-form-unit-label">% Fill</div>
                                <NumberBox id="percentFill"
                                    format="#0.000" readOnly={!canEdit}
                                    onValueChanged={onProductValueChanged}
                                    value={product ? product.percentFill : undefined} />
                            </div>

                            <div className="pw-form-unit">
                                <Button className="pw-text-button-right textbox-button"
                                    icon="percent" disabled={!canEdit}
                                    type="success"
                                    hint="Apply % To Target Fill"
                                    width={'2.6em'}
                                    height={'2.1em'}
                                    onClick={applyTargetPercentage} />
                            </div>
                        </div>

                        <div className="pw-comp-row">
                            <div className="pw-form-unit pw-comp-15">
                                <div className="pw-form-unit-label">Fill Instructions Rendered</div>
                                <div className="pw-readonly-inline-label">{fillInstructionsRender()}</div>
                            </div>
                            {product?.productState === ProductState.Bulk &&
                                <div className="pw-form-unit pw-comp-10">
                                    <div className="pw-form-unit-label">UOM</div>
                                    <SelectBox id="unitSizeUom"
                                        className="selectbox"
                                        placeholder='NA'
                                        inputAttr={{ id: "txtUom" }}
                                        readOnly={!canEdit}
                                        itemRender={DataTemplates.ProductUomItemRender}
                                        displayExpr={'name'} valueExpr={'id'}
                                        dataSource={lookupData && lookupData.uoms ?
                                            lookupData.uoms : []}
                                        onValueChanged={onProductLookupItemSelectionChanged}
                                        value={product && product.unitSizeUom ?
                                            product.unitSizeUom.id : null} />
                                </div>
                            }
                        </div>

                        <div className="pw-comp-row">
                            <div className="pw-form-unit pw-comp-30">
                                <div className="pw-form-unit-label">Components Actual Layout</div>
                                <SelectBox id="gravimetricCalculation" className="selectbox"
                                    placeholder='NA' readOnly={!canEdit}
                                    displayExpr={'name'} valueExpr={'id'}
                                    inputAttr={gravimetricCalculationAttr}
                                    itemRender={DataTemplates.ProductGravimetricCalculationItemRender}
                                    dataSource={lookupData && lookupData.gravimetricCalculationTypes ?
                                        lookupData.gravimetricCalculationTypes : []}
                                    onValueChanged={onProductLookupItemSelectionChanged}
                                    value={product && product.gravimetricCalculation ?
                                        product.gravimetricCalculation.id : null} />
                            </div>
                        </div>

                        {product && product.gravimetricCalculation &&
                            product.gravimetricCalculation.id === GravimetricCalculationType.WW_BD &&
                            <div className="pw-comp-row">
                                <div className="pw-form-unit pw-comp-20">
                                    <div className="pw-form-unit-label">Estimated Batch Density (g/mL)</div>
                                    <NumberBox id="estimatedBatchDensity" className="textbox"
                                        readOnly={!canEdit}
                                        onValueChanged={onProductValueChanged}
                                        format="0.####"
                                        value={product && product.estimatedBatchDensity ?
                                            product.estimatedBatchDensity : 0} >
                                        <Validator>
                                            <RangeRule message="Estimated Batch Density (g/mL) must be between 0 and 9.9999" min={0} max={9.9999} />
                                        </Validator>
                                    </NumberBox>
                                </div>

                                <div className="pw-form-unit pw-comp-20">
                                    <div className="pw-form-unit-label">Measured Batch Densities</div>
                                    {batchWeightsList()}
                                </div>
                            </div>
                        }

                        <div className="pw-comp-row">
                            <div className="pw-form-unit pw-comp-15">
                                <div className="pw-form-unit-label">Storage Temperature</div>
                                <div className="pw-readonly-label">{storageTemperature()}</div>
                            </div>
                        </div>

                        <div className="pw-form-unit">
                            <div className="pw-form-unit-label">Light and Humidity</div>
                            <div className="pw-readonly-label">{humidityInformation()}</div>
                            {(product && product.humidityDescription) &&
                                <div className="pw-readonly-label">{product.humidityDescription}</div>
                            }
                        </div>

                        <div className="pw-comp-row">
                            <div className="pw-form-unit">
                                <div className="pw-form-unit-label">Package Control</div>
                                <SelectBox id="packageControl" className="selectbox"
                                    placeholder='NA' readOnly={!canEdit}
                                    displayExpr={'name'} valueExpr={'id'}
                                    inputAttr={packageControlAttr}
                                    itemRender={DataTemplates.ProductPackageControlItemRender}
                                    dataSource={lookupData && lookupData.packageControlTypes ?
                                        lookupData.packageControlTypes : []}
                                    onValueChanged={onProductLookupItemSelectionChanged}
                                    value={product && product.packageControl ?
                                        product.packageControl.id : null} />
                            </div>
                        </div>

                        <NoteViewer height='10rem' title="Prep Instructions"
                            noteType={NoteType.PreparationInstructions} parentId={productId} />

                        <DmaFooter />

                    </div>


                    {/*{(product && (product.productState !== ProductState.Raw && product.productState !== ProductState.FinishedGood)) &&*/}


                    {analyticalTabsVisible &&

                        <div id="compositionsPanel">
                            <div id="product-raw-materials-datagrid-container" className="pw-margin-top-05">

                                <div className="pw-form-unit pw-comp-15">
                                    <div className="pw-form-unit-label">Form</div>
                                    <SelectBox id="form" className="selectbox"
                                        placeholder='NA' readOnly={!canEdit}
                                        itemRender={DataTemplates.ProductFormItemRender}
                                        displayExpr={'name'} valueExpr={'id'}
                                        inputAttr={formAttr}
                                        dataSource={lookupData && lookupData.formTypes ?
                                            lookupData.formTypes : []}
                                        onValueChanged={onProductLookupItemSelectionChanged}
                                        value={product && product.form ? product.form.id : null} />
                                </div>

                                <ProductCompositionsData productId={productId}
                                    catalogNumber={product && product.catalogNumber ? product.catalogNumber : undefined} isReadOnly={!canEdit} />
                            </div>
                        </div>
                    }

                    {/*{(product && (product.productSate !== ProductState.Raw && product.productState !== ProductState.FinishedGood)) &&*/}

                    {analyticalTabsVisible &&
                        <div id="compStandardsPanel">
                            <div id="compstandards-datagrid-container" className="pw-margin-top-05">
                                <ProductStandardsData productId={productId}
                                    isReadOnly={!canEdit} onProductStandardsUpdated={onProductStandardsUpdated} />
                            </div>

                            <DmaFooter />

                        </div>
                    }

                    {/*{(product && (product.productState !== ProductState.Raw && product.productState !== ProductState.FinishedGood)) &&*/}

                    {analyticalTabsVisible &&
                        <div id="analyticalPlansPanel">
                            <div className="pw-margin-bottom-05 pw-margin-top-05">
                                <ProductAnalyticalPlansData productId={productId}
                                    lookupData={lookupData} updated={productDataUpdatedIdx}
                                    isReadOnly={!canEdit} />

                                <div className="pw-margin-top-1">
                                    <div className="lq-header">
                                        <div className="lq-header-title">Monographs</div>
                                    </div>
                                    <DocumentLoader parentId={productId}
                                        documentType={DocumentType.AnalyticalPlanMonograph}
                                        readOnly={!canEdit} canDelete={canDelete} />
                                </div>

                                <div className="pw-margin-top-05">
                                    <div className="pw-form-unit-label">Product General Specification</div>
                                    <TextNoteEditor key={`MD-${key}`} parentId={productId}
                                        noteType={NoteType.ProductGeneralSpec} height="6rem" />
                                </div>

                                <DmaFooter />

                            </div>
                        </div>
                    }

                    {/*{(product && (product.productState !== ProductState.Raw && product.productState !== ProductState.FinishedGood)) &&*/}

                    {analyticalTabsVisible &&
                        <div id="coaPanel" className="pw-form-section tab-full-width">
                            <div className="pw-form-section">
                                <div className="lq-sub-title pw-margin-left-05 pw-margin-top-05 pw-margin-bottom-05">Certificates of Analysis</div>

                                <div className="pw-comp-row">
                                    <div className="pw-form-unit pw-comp-15">
                                        <div className="pw-form-unit-label">Units Displayed on COA</div>
                                        <SelectBox id="analyticalUnits" className="selectbox"
                                            placeholder='NA' disabled={!canEdit}
                                            displayExpr={'name'} valueExpr={'id'}
                                            inputAttr={unitsDisplayedOnCoaAttr}
                                            itemRender={DataTemplates.ProductUnitsDisplayedOnCoaItemRender}
                                            dataSource={lookupData && lookupData.analyticalUnitTypes ?
                                                lookupData.analyticalUnitTypes : []}
                                            onValueChanged={onProductLookupItemSelectionChanged}
                                            value={product && product.analyticalUnits ?
                                                product.analyticalUnits.id : null} />
                                    </div>

                                    <div className="pw-form-unit pw-comp-15">
                                        <div className="pw-form-unit-label">Uncertainty Multiplier</div>
                                        <NumberBox id="uncertaintyMultiplier" className="textbox"
                                            disabled={!canEdit}
                                            onValueChanged={onProductValueChanged}
                                            format="0.####"
                                            value={product && product.uncertaintyMultiplier ?
                                                product.uncertaintyMultiplier : 0} >
                                            <Validator>
                                                <RangeRule message="Uncertainty Multiplier must be between 0 and 9.999" min={0} max={9.999} />
                                            </Validator>
                                        </NumberBox>
                                    </div>

                                    <div className="pw-form-unit pw-comp-15 pw-margin-top-1">
                                        <CheckBox id="disablePurityCorrection" className="pw-check-box"
                                            text="Disable Purity Correction" disabled={!canEdit}
                                            onValueChanged={onProductValueChanged}
                                            value={product ? product.disablePurityCorrection : false} />
                                    </div>
                                </div>

                                <div className="pw-comp-row">
                                    <div className="pw-form-unit pw-comp-20">
                                        <div className="pw-form-unit-label">Preparation Design</div>
                                        <SelectBox id="preparationDesign" className="selectbox"
                                            placeholder='NA' disabled={!canEdit}
                                            displayExpr={'name'}
                                            valueExpr={'id'}
                                            inputAttr={preparationDesignAttr}
                                            itemRender={DataTemplates.ProductPreparationDesignItemRender}
                                            dataSource={lookupData && lookupData.preparationDesignTypes ?
                                                lookupData.preparationDesignTypes : []}
                                            onValueChanged={onProductLookupItemSelectionChanged}
                                            value={product && product.preparationDesign ?
                                                product.preparationDesign.id : null} />
                                    </div>

                                </div>
                            </div>

                            <DmaFooter />

                        </div>
                    }

                    {/* DISTRIBUTION */}
                    <div id="distributionPanel" className="pw-form-section tab-full-width">
                        <div className="pw-form-section">
                            <div className="lq-sub-title pw-margin-left-05 pw-margin-top-05 pw-margin-bottom-05">Distribution</div>

                            <div className="pw-comp-row">

                            </div>

                            <div className="pw-comp-row">
                                <div className="pw-form-unit pw-comp-15">
                                    <div className="pw-form-unit-label">Storage Temperature</div>
                                    <div className="pw-readonly-label">{storageTemperature()}</div>
                                </div>
                            </div>
                        </div>

                        <DmaFooter />

                    </div>

                    <div id="handlingPanel" className="pw-form-section tab-full-width">
                        <div className="pw-form-section">
                            <div className="lq-sub-title pw-margin-left-05 pw-margin-top-05 pw-margin-bottom-05">Handling</div>

                            <div className="pw-form-unit lq-header-4 pw-margin-top-05">Temperature</div>
                            <div className="pw-comp-row pw-margin-top-05">
                                <div className="pw-form-unit pw-comp-20">

                                    <div className="pw-form-unit-label">Storage Temperature</div>
                                    <SelectBox id="storage" className="selectbox"
                                        displayExpr='name' valueExpr='id'
                                        placeholder='NA' inputAttr={storageAttr}
                                        dropDownOptions={{ minWidth: '30em' }}
                                        itemRender={DataTemplates.ProductStorageItemRender}
                                        dataSource={lookupData && lookupData.storageTypes ?
                                            lookupData.storageTypes : []}
                                        onValueChanged={onProductLookupItemSelectionChanged}
                                        value={product && product.storage ?
                                            product.storage.id : null} />
                                </div>

                                <div className="pw-form-unit pw-comp-20">
                                    <div className="pw-form-unit-label">Shipping Condition</div>
                                    <SelectBox id="shippingCondition" className="selectbox"
                                        placeholder='NA' disabled={!canEdit}
                                        displayExpr={'name'} valueExpr={'id'}
                                        inputAttr={shippingConditionAttr}
                                        itemRender={DataTemplates.ProductShippingConditionItemRender}
                                        dataSource={lookupData && lookupData.shippingConditionTypes ?
                                            lookupData.shippingConditionTypes : []}
                                        onValueChanged={onProductLookupItemSelectionChanged}
                                        value={product && product.shippingCondition ?
                                            product.shippingCondition.id : null} />
                                </div>
                            </div>

                            <div className="pw-form-unit lq-header-4 pw-margin-top-05">Temperature</div>
                            <div className="pw-comp-row">
                                <div className="pw-form-unit pw-comp-15 pw-margin-top-05">
                                    <CheckBox id="protectFromLight" className="pw-check-box"
                                        text="Protect from Light" readOnly={!canDelete}
                                        onValueChanged={onProductValueChanged}
                                        value={product ? product.protectFromLight : false} />
                                </div>
                            </div>

                            <div className="pw-form-unit lq-header-4 pw-margin-top-1">Humidity</div>

                            <div className="pw-comp-row">
                                <div className="pw-form-unit pw-comp-15 pw-margin-top-05">
                                    <CheckBox id="protectFromHumidity" className="pw-check-box"
                                        text="Protect from Humidity" readOnly={!canDelete}
                                        onValueChanged={onProductValueChanged}
                                        value={product ? product.protectFromHumidity : false} />
                                </div>

                                <div className="pw-form-unit pw-comp-10">
                                    <div className="pw-form-unit-label">Humidity</div>
                                    <NumberBox id="humidity" className="textbox"
                                        onValueChanged={onProductValueChanged}
                                        placeholder='NA' readOnly={!canEdit}
                                        value={product ? product.humidity : 0}
                                        format="###.#" >
                                        <Validator>
                                            <RangeRule message="Humidity must be between 0 and 999.9" min={0} max={999.9} />
                                        </Validator>
                                    </NumberBox>
                                </div>

                                {(product && product.protectFromHumidity) &&
                                    <div className="pw-form-unit pw-comp-30">
                                        <div className="pw-form-unit-label">Production Humidity Requirement</div>
                                        <TextBox id="productionHumidityRequirement" className="textbox"
                                            readOnly={!canEdit}
                                            onValueChanged={onProductValueChanged}
                                            placeholder='NA'
                                            value={product ? product.productionHumidityRequirement : ''} >
                                        </TextBox>
                                    </div>
                                }
                            </div>

                            {(product && product.protectFromHumidity) &&
                                <div className="pw-form-unit">
                                    <div className="pw-form-unit-label">Humidity Description</div>
                                    <TextBox id="humidityDescription" className="textbox"
                                        readOnly={!canEdit}
                                        onValueChanged={onProductValueChanged}
                                        placeholder='NA'
                                        value={product ? product.humidityDescription : undefined}>
                                    </TextBox>
                                </div>
                            }

                            <div className="pw-form-unit">
                                <div className="pw-form-unit-label">Packaging Description</div>
                                <TextBox id="packagingDescription" className="textbox"
                                    onValueChanged={onProductValueChanged}
                                    placeholder='NA' readOnly={!canEdit}
                                    value={product ? product.packagingDescription : undefined} >
                                    <Validator>
                                        <StringLengthRule message="Packaging Description must be less than 100 characters in length." max={100} />
                                    </Validator>
                                </TextBox>
                            </div>

                            <NoteViewer height='10rem' title="Special Handling Instructions"
                                noteType={NoteType.SpecialHandlingInstructions} parentId={productId} />

                            <DmaFooter />

                        </div>
                    </div>

                    <div id="batchesPanel">
                        {(product && (product.productState == ProductState.Intermediate || product.productState === ProductState.Bulk)) &&
                            <div id="batches-datagrid-container" className="pw-margin-top-05">
                                <DataGrid
                                    height="22rem"
                                    onSelectionChanged={onSelectedBatchChanged}
                                    allowColumnResizing={true}
                                    columnResizingMode="nextColumn"
                                    wordWrapEnabled={true}
                                    dataSource={productBatches ? productBatches : []}
                                    rowAlternationEnabled={true}
                                    showRowLines={true}
                                    showBorders={true}>

                                    <LoadPanel enabled={true} />
                                    <Selection mode={'single'} />

                                    <Column type="buttons" width='6rem'>
                                        <ColumnButton icon="trash" hint="Delete"
                                            onClick={onBatchDeleteClick} />
                                        <ColumnButton render={gotoBatchPlanningColumnButtonRender} />
                                        <ColumnButton render={gotoBatchProductionColumnButtonRender} />
                                    </Column>

                                    <Column dataField='fullBatchName' caption="Full Name" dataType="string" width='16rem' />
                                    <Column dataField='batchName' caption="Name" dataType="string" width='10rem' />
                                    <Column dataField='batchVersion' caption="Version" dataType="number" width='6rem' />
                                    <Column dataField='batchTypeName' dataType="string" width='12rem' />
                                    <Column dataField='batchDate' caption="Date" width='8rem' dataType="date" />
                                    <Column />

                                    <Toolbar>
                                        <ToolBarItem location='before'>
                                            <div className="pw-datagrid-toolbar-template">
                                                <div className="pw-datagrid-toobar-title">Batches</div>
                                            </div>
                                        </ToolBarItem>
                                        <ToolBarItem location='after'>
                                            <Button type='normal' hint='Copy Batch' icon='repeat' id="copyBatchButton"
                                                width='1.8rem'
                                                onClick={copyBatchPrompt} />
                                        </ToolBarItem>
                                        <ToolBarItem location='after'>
                                            <Button type='normal' hint='Add New Batch' icon='plus' id="addNewBatchButton"
                                                width='1.8rem'
                                                onClick={showNewBatchPrompt} />
                                        </ToolBarItem>
                                        <ToolBarItem location='after'>
                                            <Button type='success' width={'1.8rem'} icon='clock'
                                                text=''
                                                hint='Batches Audit Information'
                                                onClick={showBatchesAuditInformationDialog} />
                                        </ToolBarItem>
                                    </Toolbar>

                                </DataGrid>
                            </div>
                        }

                        {(product && product.productState === ProductState.Raw) &&
                            <div className="pw-margin-top-05">
                                <ProductRawMaterialsData productId={productId} isReadOnly={!canEdit}
                                    vendorPartNumber={product && product.vendorPartNumber}
                                    vendorPartName={product && product.vendorPartName} />
                            </div>
                        }
                    </div>

                    <div id="notesPanel" className="tab-full-width">
                        <div className="pw-comp-row-scroll-fixed-grid">
                            <NoteViewer height='16rem' title="Product Planning Notes"
                                noteType={NoteType.ProductPlanning} parentId={productId} />
                        </div>

                        <div className="lq-sub-title pw-margin-top-1 pw-margin-bottom-05">Documents</div>
                        <DocumentLoader parentId={productId}
                            documentType={DocumentType.ProductPlanning}
                            readOnly={!canEdit} canDelete={canDelete} />

                    </div>
                </div>
            </div>

            {addProductStateDialogVisible &&
                <DialogPopup
                    visible={addProductStateDialogVisible}
                    onHiding={hideAddProductStateDialog}
                    dragEnabled={false}
                    hideOnOutsideClick={false}
                    showTitle={true}
                    title="Add New Product State"
                    width='30rem' height='20rem'>
                    <div>
                        <div className="pw-comp-row">
                            <div className="pw-form-unit pw-comp-15">
                                <div className="pw-form-unit-label">New Product State</div>
                                <div className={newProductStateColor()}>{newProductObject && newProductObject.productStateName ?
                                    newProductObject.productStateName : null}</div>

                            </div>
                        </div>

                        <div className="pw-comp-row">
                            <div className="pw-form-unit pw-comp-10">
                                <CheckBox id="isNewPartNumber" className="pw-check-box"
                                    text="Create New Part#" readOnly={!canDelete}
                                    onValueChanged={onNewProductStateChanged}
                                    value={newProductObject && newProductObject.isNewPartNumber ?
                                        newProductObject.isNewPartNumber : false} />
                            </div>

                            <div className="pw-form-unit pw-comp-10">
                                <TextBox id="newPartNumber" className="selectbox"
                                    placeholder='NA' readOnly={!canEdit}
                                    disabled={!newProductObject || !newProductObject.isNewPartNumber}
                                    onValueChanged={onNewProductStateChanged}
                                    value={newProductObject && newProductObject.newPartNumber ?
                                        newProductObject.newPartNumber : null} />
                            </div>
                        </div>

                        <div className="pw-comp-row pw-margin-top-1">
                            <div className="pw-centered-buttons-container">
                                <Button id="applySelectionButton"
                                    className="pw-button"
                                    text="Create New Product" type="success"
                                    hint="apply selection"
                                    height='2.1rem'
                                    onClick={onApplyNewProductStateClick} />

                                <Button id="applySelectionButton"
                                    className="pw-button"
                                    text="Create Working Product" type="success"
                                    hint="apply selection"
                                    height='2.1rem'
                                    onClick={onCreateWorkingProductStateClick} />

                                <Button id="cancelButton"
                                    className="pw-button"
                                    text="Cancel" type="normal"
                                    hint="cancel"
                                    height='2.1rem'
                                    onClick={onCancelNewProductClick} />
                            </div>
                        </div>

                    </div>
                </DialogPopup>
            }

            {
                newBatchPromptVisible &&
                <DialogPopup
                    visible={newBatchPromptVisible}
                    onHiding={hideNewBatchPrompt}
                    dragEnabled={false}
                    hideOnOutsideClick={false}
                    showTitle={true}
                    title={copyBatchSourceBatchId ? "Copy Batch" : "Add New Batch"}
                    width='30rem'
                    height='30rem'>
                    <AddBatchComponent productId={productId}
                        copyBatch={copyBatchSourceBatchId ? true : false}
                        selectedBatch={copyBatchSourceBatchId && selectedBatch ? selectedBatch : undefined}
                        onClose={hideNewBatchPrompt} />
                </DialogPopup>
            }

            {
                symbolEditorProps && symbolEditorProps.editorVisible &&
                <DialogPopup
                    visible={symbolEditorProps.editorVisible}
                    onHiding={hideSymbolEditor}
                    dragEnabled={false}
                    hideOnOutsideClick={false}
                    showTitle={true}
                    title="Symbol Editor"
                    width={700}
                    height={450}>

                    <TextSymbolEditor key={`se${key}`}
                        textInput={symbolEditorProps.inputText}
                        propName={symbolEditorProps.propName}
                        onApply={textSymbolApply}
                        onCancel={textSymbolCancel} />
                </DialogPopup>
            }

            {
                auditInformationDialogVisible &&
                <DialogPopup visible={auditInformationDialogVisible}
                    onHiding={hideAuditInformationDialog}
                    title="Audit History"
                    minWidth={'50em'}
                    minHeight={'32em'}
                    dragEnabled={true}
                    resizeEnabled={true}>
                    <AuditHistoryComponent entityNumber={auditEntity}
                        key={`AuditHistory${key}`}
                        childData={auditInformationShowChildData}
                        rowPointer={auditRowPointer ? auditRowPointer : undefined} />
                </DialogPopup>
            }

            {
                itemBasicInformationDialogVisible &&
                <DialogPopup visible={itemBasicInformationDialogVisible}
                    onHiding={hideItemBasicInformationDialog}
                    title="Item Basic Information"
                    width={'25rem'}
                    height={'21rem'}
                    dragEnabled={false}
                    resizeEnabled={false}>
                    <ItemBasicInformationDialog initInformation={itemBasicInformation}
                        onOkClick={hideItemBasicInformationDialog}
                        key={`IBI${key}`} />
                </DialogPopup>
            }

            {
                ticketLogEntryDialogVisible &&
                <DialogPopup visible={ticketLogEntryDialogVisible}
                    onHiding={hideTicketLogEntryDialog}
                    title="Ticket Log Entry"
                    width={'55rem'}
                    height={'45rem'}
                    dragEnabled={true}
                    resizeEnabled={true}>
                    <TicketLogEntryForm formName="Product Planning" contextData={product}
                        onCancel={hideTicketLogEntryDialog}
                        onComplete={hideTicketLogEntryDialog}
                        key={`TLE${key}`} />
                </DialogPopup>
            }

            {
                globalDialogVisible &&
                <GlobalDialog metadata={globalDialogMetadata} />
            }
        </div >
    );

    function onProductStandardsUpdated() {
        setProductDataUpdatedIdx(productDataUpdatedIdx + 1);
    }

    async function productAnalysisScopeTypesChanged(e: any) {
        if (e.name === 'selectedItemKeys' && product) {

            console.log("productAnalysisScopeTypesChanged()");
            console.log(e);

            setProductAnalysisScopesSelectedItemKeys(e.value);

            //before items (as enum array)
            //const bid = e.previousValue.map(p => p.id);
            //after items (as enum array)
            //const aid = e.value.map(a => a.id);

            const removedItems = e.previousValue.filter(b => !e.value.includes(b));
            const addedItems = e.value.filter(a => !e.previousValue.includes(a));

            if (removedItems && removedItems.length > 0) {
                await deleteProductScope(removedItems[0])
                    .catch((res) => {
                        showMessage(res, ModalMessageType.Error, "Error Removing Product Scope");
                    });
            }
            else if (addedItems && addedItems.length > 0) {
                let analysisScope: AnalysisScope = {
                    parentId: productId,
                    analysisScopeTypeId: addedItems[0]
                };

                await insertProductAnalysisScope(analysisScope)
                    .catch((res) => {
                        showMessage(res, ModalMessageType.Error, "Error Adding Product Scope");
                    });
            }
        }
    }

    function addProductState() {
        let newProduct: NewProductObject = {
            productId: productId
        };

        if (product && product.productState) {
            switch (product.productState) {
                case ProductState.Raw:
                    newProduct.productState = ProductState.Bulk;
                    break;
                case ProductState.Bulk:
                    newProduct.productState = ProductState.Intermediate;
                    break;
                case ProductState.Intermediate:
                    newProduct.productState = ProductState.FinishedGood;
                    break;
            }


            newProduct.productStateName = productStatesList.find(s => s.id === newProduct.productState).name;

            setNewProductObject(newProduct);

            showAddProductStateDialog();
        }
    }

    async function onCreateWorkingProductStateClick() {
        if (newProductObject) {
            const newProduct: NewProductRequest = {
                originalProductId: productId,
                originalProductState: product.productState,
                originalPartNumber: product.partNumber,
                newPartNumber: newProductObject.newPartNumber,
                newProductState: ProductState.Working
            };

            await addNewProductState(newProduct)
        }

        hideAddProductStateDialog();
    }

    async function onApplyNewProductStateClick() {
        if (newProductObject) {
            const newProduct: NewProductRequest = {
                originalProductId: productId,
                originalProductState: product.productState,
                originalPartNumber: product.partNumber,
                newPartNumber: newProductObject.newPartNumber,
                newProductState: newProductObject.productState
            };

            await addNewProductState(newProduct)

        }

        hideAddProductStateDialog();
    }

    function onCancelNewProductClick() {
        hideAddProductStateDialog();
    }

    //changed events

    function onProductValueChanged(e: any) {
        if (!e.event) return;

        const caller = e.element.id as string;

        if (caller && product) {
            const p = product as any;
            const currentValue = p[caller];

            const newValue = typeof e.value === "string" ? e.value.trim() : e.value;

            if (currentValue === newValue) return;
            if (!currentValue && !newValue) return;

            // Update the UI value first so if validation fails, the validation UI is displayed with the attempted value
            setProduct({ ...product, [caller]: newValue });

            // If this editor has a Validator, validate it before updating
            if (e.component.option("isValid") === false) return;

            console.log("about to update product change in db...");

            let fieldUpdate: FieldUpdate = {
                field: caller,
                value: newValue,
                rowId: productId
            };

            updateProductField(fieldUpdate)
                .catch((res) => {
                    showMessage(res, ModalMessageType.Error, "Error Updating Product Field");
                })
        }

    }

    async function onNewProductStateChanged(e: any) {
        if (!e.event)
            return;

        if (!newProductObject)
            return;

        console.log("onNewProductStateChanged");
        console.log(e);

        const caller = e.element.id as string;

        setNewProductObject({ ...newProductObject, [caller]: e.value });
    }

    async function shelfLifeFieldChanged(e: any) {
        if (!e.event)
            return;

        const caller = e.element.id as string;

        setProduct({
            ...product, shelfLife: {
                ...product.shelfLife,
                [caller]: e.value
            }
        });

        if (caller && productId && product &&
            product.shelfLife && product.shelfLife.productShelfLifeId) {
            let fieldUpdate: FieldUpdate = {
                field: caller,
                value: e.value,
                rowId: product.shelfLife.productShelfLifeId,
                parentId: productId
            };

            await updateProductShelfLife(fieldUpdate)
                .catch((res) => {
                    showMessage(res, ModalMessageType.Error, "Error Updating SLC Field");
                })
        }
    }

    function onSelectedBatchChanged(e: any) {
        if (e.selectedRowsData && e.selectedRowsData.length > 0)
            setSelectedBatch(e.selectedRowsData[0]);
    }

    function onProductLookupItemSelectionChanged(e: any) {
        if (!lookupData)
            return;

        const caller = e.element.id;
        let prevItem = getLookupItem(caller, e.previousValue);
        let newItem = getLookupItem(caller, e.value);

        if (prevItem !== null && newItem !== null) {
            let fieldUpdate: FieldUpdate = {
                field: caller,
                value: e.value,
                rowId: product.shelfLife.productShelfLifeId,
                parentId: productId
            };

            updateProductField(fieldUpdate)
                .catch((res) => {
                    showMessage(res, ModalMessageType.Error, "Error Updating Product Lookup Item");
                });

            setProduct({ ...product, [caller]: newItem });
        }
    }

    function textSymbolApply(data: TextSymbolEditorData) {
        if (symbolEditorProps && data) {
            let propName = data.propName;

            if (propName === "chemicalFormula") {
                let fieldUpdate: MolecularDataFieldUpdate = {
                    field: "formula",
                    value: data.text,
                    fieldUpdateType: FieldUpdateType.Data,
                    rowId: product ? product.molecularDataId : ""
                };

                updateMolecularDataField(fieldUpdate)
                    .catch((res) => {
                        showMessage(res, ModalMessageType.Error, "Error Applying Text Symbol");
                    });

                if (product && product.molecularData) {
                    let molecularData = product.molecularData;
                    molecularData.formula = data.text;

                    setProduct({ ...product, molecularData: molecularData });
                }
            }
            else {
                let fieldUpdate: FieldUpdate = {
                    field: propName,
                    value: data.text,
                    rowId: product.productId,
                    parentId: productId
                };

                updateProductField(fieldUpdate)
                    .catch((res) => {
                        showMessage(res, ModalMessageType.Error, "Error Applying Text Symbol");
                    });

                setProduct({ ...product, [propName]: data.text });
            }

            hideSymbolEditor();
        }
    }

    function textSymbolCancel() {
        hideSymbolEditor();
    }

    // TODO
    function onBatchDeleteClick(e: any) {

    }

    //get functions

    async function getProduct() {
        await CrudFunctions.getData(`products/product/${productId}`)
            .then((data) => {
                let product = data as Product;

                console.log("PROD:");
                console.log(data);

                if (product) {
                    const analysisScopes = product.analysisScopeTypes.map(s => s.analysisScopeTypeId);
                    setProductAnalysisScopesSelectedItemKeys(analysisScopes);
                }

                setProduct(product);
            })
            .catch((res) => {
                showMessage(res, ModalMessageType.Error, "Error Retrieving Product");
            });
    }

    async function getProductStates() {
        await CrudFunctions.getData(`products/productstates/${productId}`)
            .then((data) => {
                setProductStates(data);
            })
            .catch((res) => {
                showMessage(res, ModalMessageType.Error, "Error Retrieving Product States");
            });
    }

    async function getProductBatches() {
        await CrudFunctions.getData(`products/batcheslist/${productId}`)
            .then((data) => {
                setProductBatches(data);
            })
            .catch((res) => {
                showMessage(res, ModalMessageType.Error, "Error Retrieving Product");
            });
    }

    async function getLookupData() {
        await CrudFunctions.getData("common/productslookupdata")
            .then((data) => {
                setLookupData(data);
            })
            .catch((res) => {
                showMessage(res, ModalMessageType.Error, "Error Retrieving Lookup Data");
            });
    }

    async function getVendors() {
        await CrudFunctions.getData("common/vendors")
            .then((data) => {
                setVendors(data);
            })
            .catch((res) => {
                showMessage(res, ModalMessageType.Error, "Error Retrieving Vendors");
            });
    }

    /*
    async function getShelfLifeDocuments() {
        await CrudFunctions.getData(`media/documentsbyparentid/${productId}/${DocumentType.ShelfLifeSupport}`)
            .then(data => {
                setShelfLifeSupportDocuments(data);
            })
            .catch((res) => {
                showMessage(res, ModalMessageType.Error, "Error Retrieving SLC Documents");
            });
    }
    */

    // POST function

    async function addNewProductState(request: NewProductRequest) {
        await CrudFunctions.postData("products/newproductstate", request)
            .then((data) => {
                console.log("NEW PROD STATE ADDED");
                console.log(data);

                if (data && data.newProductId)
                    setNewProductUrl(`/product/${data.newProductId}`);
            })
            .catch((res) => {
                showMessage(res, ModalMessageType.Error, "Error Adding New Product State");
            });
    }

    async function insertProductAnalysisScope(analysisScope: AnalysisScope) {
        return await CrudFunctions.postData("products/analysisscope", analysisScope);
    }

    //PUT functions

    async function updateProductField(fieldUpdate: FieldUpdate) { //   fieldName: string, value: any) {

        console.log("IN updateProductField");
        console.log(fieldUpdate);


        /*
        let fieldUpdate: FieldUpdate = {
            field: fieldName,
            value: value,
            rowId: productId
        };

        console.log("fieldUpdate:");
        console.log(fieldUpdate);
        */

        return CrudFunctions.putData("products/productfield", fieldUpdate);
    }

    async function updateProductShelfLife(fieldUpdate: FieldUpdate) {
        return CrudFunctions.putData("shelflife/productshelflifefield", fieldUpdate);
    }

    async function updateMolecularDataField(fieldUpdate: MolecularDataFieldUpdate) {
        return CrudFunctions.putData("products/moleculardatafield", fieldUpdate);
    }

    async function deleteProductScope(analysisScopeTypeId: number) {
        return CrudFunctions.deleteData(`products/analysisscope/${productId}/${analysisScopeTypeId}`);
    }

    //validation functions

    function getLookupItem(collection: string, value: number): LookupItem | null {
        if (!lookupData)
            return null;

        let lookupItems: LookupItem[] = [];

        switch (collection) {
            case "scope":
                lookupItems = lookupData.scopeTypes.filter(f => f.id === value);
                break;
            case "form":
                lookupItems = lookupData.formTypes.filter(f => f.id === value);
                break;
            case "packageControl":
                lookupItems = lookupData.packageControlTypes.filter(f => f.id === value);
                break;
            case "storage":
                lookupItems = lookupData.storageTypes.filter(f => f.id === value);
                break;
            case "shippingCondition":
                lookupItems = lookupData.shippingConditionTypes.filter(f => f.id === value);
                break;
            case "analyticalUnit":
            case "analyticalUnits":
                lookupItems = lookupData.analyticalUnitTypes.filter(f => f.id === value);
                break;
            case "preparationDesign":
                lookupItems = lookupData.preparationDesignTypes.filter(f => f.id === value);
                break;
            case "unitSizeUom":
                lookupItems = lookupData.uoms.filter(f => f.id === value);
                break;
            case "gravimetricCalculation":
                lookupItems = lookupData.gravimetricCalculationTypes.filter(f => f.id === value);
                break;
        }

        if (lookupItems !== null && lookupItems.length > 0)
            return lookupItems[0];

        return null;
    }

    //navigation

    function onPanelButtonClick(e: any) {
        resetAllButtons();

        let id: string = e.target.id;

        if (e.target.tagName === "SPAN") {
            id = e.target.parentElement.id;

            const button = e.target.parentElement as HTMLDivElement;

            if (button)
                button.className = "tab-button-active";
        }
        else {
            const button = e.target as HTMLDivElement;

            if (button)
                button.className = "tab-button-active";
        }

        hideAllComponents();

        const panel = document.getElementById(id.replace("Button", "")) as HTMLDivElement;
        if (panel)
            panel.style.display = "";
    }

    function resetAllButtons() {
        if (!product)
            return;

        const mainPanelButton = document.getElementById("mainPanelButton") as HTMLDivElement;
        mainPanelButton.className = "tab-button-inactive";

        const molecularDataPanelButton = document.getElementById("molecularDataPanelButton") as HTMLDivElement;
        molecularDataPanelButton.className = "tab-button-inactive";

        const deaPanelButton = document.getElementById("deaPanelButton") as HTMLDivElement;
        deaPanelButton.className = "tab-button-inactive";

        const shelfLifeClaimPanelButton = document.getElementById("shelfLifeClaimPanelButton") as HTMLDivElement;
        shelfLifeClaimPanelButton.className = "tab-button-inactive";

        const safetyPanelButton = document.getElementById("safetyPanelButton") as HTMLDivElement;
        safetyPanelButton.className = "tab-button-inactive";

        const handlingPanelButton = document.getElementById("handlingPanelButton") as HTMLDivElement;
        handlingPanelButton.className = "tab-button-inactive";

        const batchesPanelButton = document.getElementById("batchesPanelButton") as HTMLDivElement;
        batchesPanelButton.className = "tab-button-inactive";

        const notesPanelButton = document.getElementById("notesPanelButton") as HTMLDivElement;
        notesPanelButton.className = "tab-button-inactive";

        const productionPanelButton = document.getElementById("productionPanelButton") as HTMLDivElement;
        if (productionPanelButton)
            productionPanelButton.className = "tab-button-inactive";

        const compositionsPanelButton = document.getElementById("compositionsPanelButton") as HTMLDivElement;
        if (compositionsPanelButton)
            compositionsPanelButton.className = "tab-button-inactive";

        const compStandardsPanelButton = document.getElementById("compStandardsPanelButton") as HTMLDivElement;
        if (compStandardsPanelButton)
            compStandardsPanelButton.className = "tab-button-inactive";

        const analyticalPlansPanelButton = document.getElementById("analyticalPlansPanelButton") as HTMLDivElement;
        if (analyticalPlansPanelButton)
            analyticalPlansPanelButton.className = "tab-button-inactive";

        const coaPanelButton = document.getElementById("coaPanelButton") as HTMLDivElement;
        if (coaPanelButton)
            coaPanelButton.className = "tab-button-inactive";

        const distributionPanelButton = document.getElementById("distributionPanelButton") as HTMLDivElement;
        if (distributionPanelButton)
            distributionPanelButton.className = "tab-button-inactive";

    }

    function hideAllComponents(onload: boolean = false) {
        if (!product)
            return;

        if (!onload) {
            const mainPanel = document.getElementById("mainPanel") as HTMLDivElement;
            if (mainPanel)
                mainPanel.style.display = "none";
            else
                return;
        }

        const molecularDataPanel = document.getElementById("molecularDataPanel") as HTMLDivElement;
        if (molecularDataPanel)
            molecularDataPanel.style.display = "none";

        const deaPanel = document.getElementById("deaPanel") as HTMLDivElement;
        if (deaPanel)
            deaPanel.style.display = "none";

        const shelfLifeClaimPanel = document.getElementById("shelfLifeClaimPanel") as HTMLDivElement;
        if (shelfLifeClaimPanel)
            shelfLifeClaimPanel.style.display = "none";

        const safetyPanel = document.getElementById("safetyPanel") as HTMLDivElement;
        if (safetyPanel)
            safetyPanel.style.display = "none";

        const handlingPanel = document.getElementById("handlingPanel") as HTMLDivElement;
        if (handlingPanel)
            handlingPanel.style.display = "none";

        const batchesPanel = document.getElementById("batchesPanel") as HTMLDivElement;
        if (batchesPanel)
            batchesPanel.style.display = "none";

        const notesPanel = document.getElementById("notesPanel") as HTMLDivElement;
        if (notesPanel)
            notesPanel.style.display = "none";

        const productionPanel = document.getElementById("productionPanel") as HTMLDivElement;
        if (productionPanel)
            productionPanel.style.display = "none";

        const coaPanel = document.getElementById("coaPanel") as HTMLDivElement;
        if (coaPanel)
            coaPanel.style.display = "none";

        const distributionPanel = document.getElementById("distributionPanel") as HTMLDivElement;
        if (distributionPanel)
            distributionPanel.style.display = "none";

        const compositionsPanel = document.getElementById("compositionsPanel") as HTMLDivElement;
        if (compositionsPanel)
            compositionsPanel.style.display = "none";

        const compStandardsPanel = document.getElementById("compStandardsPanel") as HTMLDivElement;
        if (compStandardsPanel)
            compStandardsPanel.style.display = "none";

        const analyticalPlansPanel = document.getElementById("analyticalPlansPanel") as HTMLDivElement;
        if (analyticalPlansPanel)
            analyticalPlansPanel.style.display = "none";
    }

    //click events

    function showProductAuditInformationDialog() {
        setAuditEntity(Entities.Products);
        setAuditRowPointer(productId);
        setAuditInformationShowChildData(false);
        setAuditInformationDialogVisible(true);
    }

    function showProductInformationDialog() {
        if (product) {
            let itemInformation: ItemBasicInformation = {
                rowPointer: productId,
                createTimestamp: product.createTimestamp,
                lastModifiedTimestamp: product.modifyTimestamp,
                createUser: product.createUser,
                modifyUser: product.modifyUser
            };

            setItemBasicInformation(itemInformation);
            setItemBasicInformationDialogVisible(true);
        }
    }

    // show/hide

    function showNewBatchPrompt() {
        setNewBatchPromptVisible(true);
    }

    function copyBatchPrompt() {
        if (!selectedBatch) {
            showInformationMessage(ModalMessageType.Information, "No Selected Batch", "You must first select a batch");
            return;
        }

        setCopyBatchSourceBatchId(selectedBatch.batchId);
        setNewBatchPromptVisible(true);
    }

    function showAddProductStateDialog() {
        setAddProductStateDialogVisible(true);
    }

    function hideAddProductStateDialog() {
        setAddProductStateDialogVisible(false);
    }

    function hideNewBatchPrompt() {
        setCopyBatchSourceBatchId(null);
        setNewBatchPromptVisible(false);

        getProductBatches();
    }

    function showSymbolEditor(propName: string, text?: string) {
        let symbolEditorParams: SymbolEditorProps = {
            inputText: text,
            propName: propName,
            editorVisible: true
        };

        setSymbolEditorProps(symbolEditorParams);
    }

    function hideSymbolEditor() {
        let symbolEditorParams = { ...symbolEditorProps };
        symbolEditorParams.editorVisible = false;

        setSymbolEditorProps(symbolEditorParams);
    }

    function hideAuditInformationDialog() {
        setAuditInformationDialogVisible(false);
    }

    function hideItemBasicInformationDialog() {
        setItemBasicInformationDialogVisible(false);
    }

    function hideTicketLogEntryDialog() {
        setTicketLogEntryDialogVisible(false);
    }

    function showBatchesAuditInformationDialog() {
        showAuditInformationDialog(Entities.BatchPlanning);
    }

    function showAuditInformationDialog(entity: Entities) {
        setAuditEntity(entity);
        setAuditRowPointer(productId);
        setAuditInformationShowChildData(true);
        setAuditInformationDialogVisible(true);
    }

    function nameLabelTextSymbolButton() {
        console.log("nameLabelTextSymbolButton::");
        console.log(product);

        if (product)
            showSymbolEditor("nameLabel", product.nameLabel);
    }

    function longNameTextSymbolButton() {
        if (product)
            showSymbolEditor("longName", product.longName);
    }

    //rendering

    function gotoBatchPlanningColumnButtonRender(e: any) {
        let batch = e.data as Batch;
        let path = `/batch/${batch.batchId}`;

        if (batch) {
            return <NavLink to={path} className="datagrid-icon-link"><span className="dx-icon-edit datagrid-icon column-button-margin"></span></NavLink>
        }
    }

    function gotoBatchProductionColumnButtonRender(e: any) {
        let batch = e.data as Batch;
        let path = `/batch-production/${batch.batchId}`;

        if (batch) {
            return <NavLink to={path} className="datagrid-icon-link"><span className="dx-icon-product datagrid-icon column-button-margin"></span></NavLink>
        }
    }

    function analysisScopesRender(scope: LookupItem) {
        return (
            <div>
                <div>{scope.name}</div>
            </div>
        );
    }

    function productStatesTreeViewitemRender(item: ProductListItem) {
        if (item && item.partNumber && item.productState) {
            let cssClass = RenderingFunctions.returnProductStateColor(item.productState);
            let boldCss = productId && productId === item.productId ? "pw-bold" : "";

            return (
                <div>
                    <div className={`pw-readonly-label pw-tree-view-item ${cssClass} ${boldCss}`}>{item.partNumber} {item.productStateName}</div>
                </div>
            );
        }

        return (
            <div></div>
        );
    }


    // other

    function applyTargetPercentage(e: any) {
        if (product && product.percentFill &&
            product.percentFill !== 0) {

            if (product.nominalUnitSize) {
                const targetFill = PWMath.roundNumber(product.nominalUnitSize +
                    (product.nominalUnitSize * (product.percentFill / 100)), 3);

                setProduct({ ...product, targetFill: targetFill });

                const targetFillUncertainty = PWMath.roundNumber(product.nominalUnitSize *
                    (product.percentFill / 100), 3);

                setProduct({ ...product, targetFillUncertainty: targetFillUncertainty });

                //TODO: this should be done as one transaction (I think there is a products/updatedfields API)
                let fieldUpdate: FieldUpdate = {
                    field: "targetFill",
                    value: targetFill,
                    rowId: productId
                };

                CrudFunctions.putData("products/productfield", fieldUpdate);

                fieldUpdate = {
                    field: "targetFillUncertainty",
                    value: targetFillUncertainty,
                    rowId: productId
                };

                CrudFunctions.putData("products/productfield", fieldUpdate);
            }
        }
    }

    function showMessage(res: any, mode: ModalMessageType, title: string) {
        let mm: ModalMetadata = {
            error: res,
            mode: mode,
            title: title
        };

        setGlobalDialogMetadata(mm);
        setGlobalDialogVisible(true);
    }

    function showInformationMessage(mode: ModalMessageType, title: string, message: string) {
        let mm: ModalMetadata = {
            error: null,
            mode: mode,
            title: title,
            message: message
        };

        setGlobalDialogMetadata(mm);
        setGlobalDialogVisible(true);
    }

    function initializeUI() {

        resetAllButtons();
        hideAllComponents(true);



        //setAnalyticalTabsVisible(false);


        //const mainPanelButton = document.getElementById("mainPanelButton") as HTMLButtonElement;
        //mainPanelButton.click();

    }

    async function getAllData() {
        await Promise.all([
            getProduct(),
            getProductStates(),
            getProductBatches(),
            getVendors(),
            getLookupData()
        ])
    }

    function updateProductStatesChanges() {
        if (productStateUiUpdated)
            return;

        if (product && (product.productState !== ProductState.Bulk && product.productState !== ProductState.Intermediate))
            setAnalyticalTabsVisible(false);
        else
            setAnalyticalTabsVisible(true);


        const mainPanelButton = document.getElementById("mainPanelButton") as HTMLDivElement;
        if (mainPanelButton)
            mainPanelButton.click();


        productStatesTreeViewRef.current?.instance().expandAll();

        let canAdd = false;

        if (product && productStates) {
            if ((product.productState === ProductState.Bulk) ||
                (product.productState === ProductState.Raw) || // && productStates.find(ps => ps.productState !== ProductState.Raw)) ||
                (product.productState === ProductState.Intermediate && productStates.find(ps => ps.productState !== ProductState.FinishedGood)))
                canAdd = true;


            console.log("updateProductStatesChanges()");
            console.log(canAdd);

            setCanAddNewProductState(canAdd);

            setProductStateUiUpdated(true);
        }
    }

}
